import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';

import {
  ButtonModule,
  DialogRef,
  IconModule,
  RadioOptionModule,
  SimpleInputModule,
  SpinnerDirective,
  TextAreaComponentModule,
} from '@pinup-teams/common';
import { TranslateModule } from '@ngx-translate/core';
import { v4 as uuid } from 'uuid';

import { AiChatFormService } from './ai-chat-form.service';
import { AiCharStore } from './store';
import { MessageDatePipe } from '../../pipes/date.pipe';
import {
  ChatMessageFeedback,
  InternalMessageStatus,
  ModelFormGroup,
} from '../../models';
import { DictionariesService } from '../../services/dictionaries.service';
import { SlackLinkDirective } from '../../directives/slack-link.directive';
import { UserChatMessageComponent } from './components/user-chat-message/user-chat-message.component';
import { BotChatMessageComponent } from './components/bot-chat-message/bot-chat-message.component';
import { InfiniteScrollDirective } from './directives/infinite-scroll.directive';
import { TranslateInPipe } from '../../pipes/translate-in.pipe';
import { questionToMessage } from './helpers/question-to-message';

@Component({
  selector: 'pu-ai-chat-form',
  standalone: true,
  imports: [
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatButton,
    MatDialogClose,
    OverlayModule,
    ButtonModule,
    IconModule,
    TranslateModule,
    SimpleInputModule,
    ReactiveFormsModule,
    MessageDatePipe,
    SpinnerDirective,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    RadioOptionModule,
    TextAreaComponentModule,
    SlackLinkDirective,
    UserChatMessageComponent,
    BotChatMessageComponent,
    InfiniteScrollDirective,
    TranslateInPipe,
  ],
  providers: [AiChatFormService, AiCharStore, TranslateInPipe],
  templateUrl: './ai-chat-form.component.html',
  styleUrl: './ai-chat-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatFormComponent {
  #dialogRef = inject(DialogRef);
  #formBuilder = inject(FormBuilder);
  #aiChatStore = inject(AiCharStore);
  #dictionariesService = inject(DictionariesService);

  internalMessageStatus = InternalMessageStatus;

  chatQuestionForm = this.#formBuilder.group({
    question: new FormControl(''),
  });

  feedbackForm = this.#formBuilder.group<ModelFormGroup<ChatMessageFeedback>>({
    feedbackType: new FormControl(null, [Validators.required]),
    text: new FormControl(''),
  });

  dislikeReasons = this.#dictionariesService.dislikeReasons;
  isLoading = this.#aiChatStore.isLoading;
  selectedChat = this.#aiChatStore.selectedChat;
  messages = this.#aiChatStore.messages;
  filters = this.#aiChatStore.filters;
  chatStarted = this.#aiChatStore.chatStarted;
  feedbackMessageId = this.#aiChatStore.feedbackMessageId;
  disableInfiniteScroll = this.#aiChatStore.disableInfiniteScroll;
  disableSendQuestion = this.#aiChatStore.disableSendQuestion;

  messagesScrolled() {
    this.#aiChatStore.updateFilters({
      offset: this.filters().offset + this.filters().limit,
    });
  }

  leaveFeedback() {
    if (this.feedbackForm.invalid) {
      return;
    }

    this.#aiChatStore.leaveFeedback({
      id: this.feedbackMessageId(),
      ...this.feedbackForm.getRawValue(),
    });

    this.feedbackForm.reset();
    this.#aiChatStore.closeFeedbackForm();
  }

  closeFeedbackForm() {
    this.#aiChatStore.closeFeedbackForm();
  }

  send() {
    const request = this.chatQuestionForm.controls.question.value;
    if (this.chatQuestionForm.invalid || !request) {
      return;
    }

    this.#aiChatStore.sendQuestion(
      questionToMessage({
        chatId: this.selectedChat().id,
        messageId: uuid(),
        chatStarted: this.chatStarted(),
        request,
      }),
    );

    this.chatQuestionForm.controls.question.patchValue('');
  }

  close() {
    this.#dialogRef.close();
  }
}
